import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CallIcon from '@mui/icons-material/Call';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

import FormDialog from '../components/forms/FormDialog';
import AddPatient from '../components/forms/AddPatient';
import AddAppointment from '../components/forms/AddAppointment';
import { fetchPatientsRequest, SelectedPatient } from 'src/modules/app/store/appActions';
import storage from 'src/utils/storageUtils';

const PatientContainer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openPatientModal, setOpenPatientModal] = useState(false);
  const [openAppointmentModal, setOpenAppointmentModal] = useState(false);
  const [selectedPatientForAppt, setSelectedPatientForAppt] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // 3-Dots Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuPatient, setMenuPatient] = useState(null);

  const staffName = localStorage.getItem('staffName') || localStorage.getItem('STAFFNAME') || 'SevenCoreTech';

  const fetchData = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    const token = storage.get('TOKEN');
    try {
      const response = await new Promise((resolve, reject) => {
        dispatch(fetchPatientsRequest(token, page, perPage, resolve, reject));
      });
      if (response && response.data) {
        setPatients(response.data);
        if (response.meta?.totalRecords) {
          setTotalRecords(response.meta.totalRecords);
        } else if (response.totalRecords) {
          setTotalRecords(response.totalRecords);
        } else {
          setTotalRecords(response.data.length);
        }

        if (response.meta?.totalPages) {
          setTotalPages(response.meta.totalPages);
        } else if (response.totalPages) {
          setTotalPages(response.totalPages);
        }
      }
    } catch (err) {
      console.error('Failed to fetch patients:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [dispatch, page, perPage]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleOpenMenu = (event, patient) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuPatient(patient);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuPatient(null);
  };

  const handleCallPatient = (patient) => {
    const rawPhone = patient?.contactNo || patient?.phone || '';
    if (rawPhone) {
      window.open(`tel:${rawPhone}`, '_self');
    } else {
      enqueueSnackbar('No phone number available for this patient', { variant: 'warning' });
    }
  };

  const sendWhatsAppMessage = (patient) => {
    const rawPhone = patient?.contactNo || patient?.phone || '';
    const digits = String(rawPhone).replace(/\D/g, '');
    let phoneParam = '';
    if (digits.length >= 11) phoneParam = digits;
    else if (digits.length === 10) phoneParam = `91${digits}`;

    if (!phoneParam) {
      enqueueSnackbar('No phone number available for WhatsApp', { variant: 'warning' });
      return;
    }

    const msg = `Hello ${patient.fullName || patient.name || 'Patient'}, greetings from MyCliniq. How can we assist you today?`;
    window.open(`https://wa.me/${phoneParam}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleViewPatientProfile = (patient) => {
    const pId = patient?.id || patient?._id;
    const pName = patient?.fullName || patient?.name || '';
    if (pId) {
      storage.set('PATIENT_ID', pId);
      storage.set('PATIENT_NAME', pName);
      storage.set('SELECTED_PATIENT', JSON.stringify({ patientId: pId, patientName: pName, patient }));
      dispatch(SelectedPatient({ id: pId, patientId: pId, patientName: pName, patient }));
      navigate(`/viewprofile/${pId}`, { state: { patientList: patients } });
    }
  };

  const handleBookAppointment = (patient) => {
    setSelectedPatientForAppt(patient);
    setOpenAppointmentModal(true);
  };

  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      const name = patient.fullName || patient.name || '';
      const phone = patient.contactNo || patient.phone || '';
      const pid = patient.patientId || patient.id || '';
      return (
        name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        phone.includes(searchQuery) ||
        pid.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [patients, searchQuery]);

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Header Banner */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Hey! {staffName} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            List of all your patients!
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Tooltip title="Refresh Patients">
            <IconButton
              onClick={() => fetchData(false)}
              disabled={loading}
              sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '10px',
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenPatientModal(true)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              bgcolor: theme.palette.primary.main,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}
          >
            Register Patient
          </Button>
        </Stack>
      </Box>

      {/* Main Content Card */}
      <Card
        sx={{
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.03)',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {/* Controls Bar */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            All Registered Patients ({totalRecords || patients.length})
          </Typography>

          <TextField
            size="small"
            placeholder="Search by name, patient ID, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
              },
            }}
          />
        </Box>

        {/* Patient Cards List */}
        <Box sx={{ p: 2.5 }}>
          {filteredPatients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PeopleAltIcon sx={{ fontSize: 48, color: theme.palette.divider, mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                No Patients Found
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 2 }}>
                Click "Register Patient" to add a new record to the registry.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpenPatientModal(true)}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                Register Patient
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              {filteredPatients.map((patient, idx) => {
                const patientName = patient.fullName || patient.name || 'Patient';
                const ageText = patient.age ? `${patient.age}` : 'N/A';
                const genderText = patient.gender ? `${patient.gender}` : '';

                return (
                  <Card
                    key={patient.id || patient._id || idx}
                    sx={{
                      borderRadius: '16px',
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                      p: 2.5,
                      boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.02)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.06)',
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {/* Top Row: Patient Name & Age • Gender */}
                    <Box sx={{ pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <Typography
                        variant="h6"
                        onClick={() => handleViewPatientProfile(patient)}
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          fontSize: '17px',
                          cursor: 'pointer',
                          '&:hover': { color: theme.palette.primary.main },
                        }}
                      >
                        {patientName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500, mt: 0.3 }}>
                        {ageText} {genderText ? `• ${genderText}` : ''}
                      </Typography>
                    </Box>

                    {/* Bottom Row: Call Button & 3-Dots Action Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, pt: 1.5 }}>
                      <Tooltip title="Call Patient">
                        <IconButton
                          size="small"
                          onClick={() => handleCallPatient(patient)}
                          sx={{
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC',
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.primary,
                            width: 38,
                            height: 38,
                            '&:hover': { bgcolor: theme.palette.primary.main, color: '#FFFFFF', borderColor: theme.palette.primary.main },
                          }}
                        >
                          <CallIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Patient Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, patient)}
                          sx={{
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F8FAFC',
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.primary,
                            width: 38,
                            height: 38,
                            '&:hover': { bgcolor: theme.palette.primary.main, color: '#FFFFFF', borderColor: theme.palette.primary.main },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Custom Pagination Footer */}
        <Box
          sx={{
            p: 2,
            px: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'transparent',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {(page - 1) * perPage + 1}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              of
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {totalRecords || patients.length}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              records
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', color: theme.palette.text.primary }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, px: 0.5 }}>
              Page <strong style={{ color: theme.palette.text.primary }}>{page}</strong>
            </Typography>

            <IconButton
              size="small"
              onClick={() => setPage((prev) => (prev < totalPages ? prev + 1 : prev))}
              disabled={page >= totalPages}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', color: theme.palette.text.primary }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Card>

      {/* 3-Dots Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            minWidth: '150px',
            py: 0.5,
            bgcolor: theme.palette.background.paper,
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleViewPatientProfile(menuPatient);
            handleCloseMenu();
          }}
          sx={{ py: 1, px: 2, gap: 1.5 }}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <PersonOutlineIcon fontSize="small" sx={{ color: theme.palette.text.primary }} />
          </ListItemIcon>
          <ListItemText
            primary="View Profile"
            primaryTypographyProps={{ fontWeight: 600, fontSize: '14px', color: theme.palette.text.primary }}
          />
        </MenuItem>
      </Menu>

      {/* Add Patient Modal */}
      <FormDialog open={openPatientModal} onClose={() => setOpenPatientModal(false)}>
        <AddPatient
          onClose={() => setOpenPatientModal(false)}
          onSave={() => {
            fetchData();
            setOpenPatientModal(false);
          }}
        />
      </FormDialog>

      {/* Add Appointment Modal */}
      <FormDialog open={openAppointmentModal} onClose={() => setOpenAppointmentModal(false)}>
        <AddAppointment
          initialPatient={selectedPatientForAppt}
          onClose={() => setOpenAppointmentModal(false)}
          onSave={() => {
            setOpenAppointmentModal(false);
            enqueueSnackbar('Appointment scheduled successfully', { variant: 'success' });
          }}
        />
      </FormDialog>
    </Box>
  );
};

export default PatientContainer;
